import { useContext, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import styled from "styled-components";
import {
  faAngleDoubleDown,
  faAngleDoubleUp,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { CliMatch } from "@types";
import { makeResultKey } from "@utils";

import { WorkbenchContext } from "@pages/editor/providers";
import { ErrorResult } from "@pages/editor/stores";
import { Highlight } from "@pages/editor/types";
import { applyFix } from "@pages/editor/utils";

import { ErrorsSection } from "./ErrorsSection";
import { MatchesSection } from "./MatchesSection";
import { ResultSummary } from "./ResultSummary";
import { TestsSection } from "./TestsSection";

interface Dimmable {
  dim?: boolean;
  error?: boolean;
}

const Details = styled.div<Dimmable>`
  border-top: 1px var(--indigo3) solid;

  & > * {
    padding: 0 var(--global-spacing-m);
    opacity: ${(props) => (props.dim ? "0.5" : "initial")};
    transition: opacity 0.2s ease;
  }
`;

const Container = styled.div``;

const SummaryBar = styled.div<Dimmable>`
  display: flex;
  align-items: center;
  gap: var(--global-spacing-m);
  padding: var(--global-spacing-xs) var(--global-spacing-m);
  justify-content: space-between;
  cursor: pointer;
  border-top: 1px var(--indigo3) solid;

  ${(props) =>
    props.error &&
    `
    background-color: var(--global-red);
    color: white;
  `}

  :hover {
    background-color: var(--indigo7);
  }

  & > * {
    opacity: ${(props) => (props.dim ? "0.5" : "initial")};
    transition: opacity 0.2s ease;
  }
`;

const EmptySummaryBar = styled(SummaryBar)`
  justify-content: space-around;
  color: var(--global-mid-gray);
  cursor: initial;

  :hover {
    background-color: initial;
  }
`;

const Panel = () => {
  const { workbench } = useContext(WorkbenchContext);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const { result, bundle } = workbench;

  useEffect(() => {
    if (bundle && result && result.errorsCount > 0) setIsExpanded(true);
  }, [bundle, result]);

  const [fixesApplied, setFixesApplied] = useState<{ [key: string]: boolean }>(
    {}
  );

  const handleApplyFix = (r: CliMatch) => () => {
    const result = workbench.result;
    if (!bundle || !result || result instanceof ErrorResult) {
      return;
    }
    const { setTargetText, targetText } = bundle;
    const highlights = result.highlights;
    const { newTarget, newHighlights } = applyFix(r, targetText, highlights);

    setFixesApplied({ ...fixesApplied, [makeResultKey(r)]: true });
    setTargetText(newTarget);

    // new targetText has to be propagated to the editor before applying the highlights
    setTimeout(() => {
      result.targetText = newTarget;
      result.overridenHighlights = newHighlights as Highlight[];
    }, 0);
  };

  if (result === null) {
    if (bundle !== null && bundle.isRunning)
      return (
        <Container>
          <EmptySummaryBar>
            <span>
              Running <code>{bundle.rule?.id}</code>…
            </span>
          </EmptySummaryBar>
        </Container>
      );

    return (
      <Container>
        <EmptySummaryBar>Run your rule to see its matches.</EmptySummaryBar>
      </Container>
    );
  }

  const expandIcon = (
    <FontAwesomeIcon
      icon={isExpanded ? faAngleDoubleDown : faAngleDoubleUp}
      size="xs"
    />
  );

  return (
    <Container>
      <Details
        dim={bundle?.isRunning}
        style={{
          display: isExpanded ? undefined : "none",
        }}
      >
        {result instanceof ErrorResult || result.errorsCount > 0 ? (
          <ErrorsSection result={result} />
        ) : (
          <>
            <MatchesSection
              matches={result.response.semgrep_result.output.results}
              onApplyFix={handleApplyFix}
              fixesApplied={fixesApplied}
              dirty={result.isDirty}
            />
            <TestsSection result={result} />
          </>
        )}
      </Details>
      <SummaryBar
        dim={bundle?.isRunning}
        error={!bundle?.isRunning && result.errorsCount > 0}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {expandIcon}
        <ResultSummary result={result} />
        {expandIcon}
      </SummaryBar>
    </Container>
  );
};

export const ResultsPanel = observer(Panel);
